import { useState, useEffect, useCallback } from 'react';

// Define the interface for app preferences
export interface AppPreferences {
    defaultDivision: 'A Division' | 'B Division';
    defaultSorting: 'Newest First' | 'Oldest First' | 'Alphabetical';
    theme: 'light' | 'dark';
}

// Default preferences - theme will be set based on system preference later
const defaultPreferences: AppPreferences = {
    defaultDivision: 'B Division',
    defaultSorting: 'Newest First',
    theme: 'light' // Default fallback, will be updated based on system preference
};

/**
 * Hook to manage application preferences
 * @returns Preferences state and update functions
 */
export const useAppPreferences = (): {
    preferences: AppPreferences;
    setDefaultDivision: (division: 'A Division' | 'B Division') => void;
    setDefaultSorting: (sorting: 'Newest First' | 'Oldest First' | 'Alphabetical') => void;
    setTheme: (theme: 'light' | 'dark') => void;
    loadPreferences: () => void;
    isLoading: boolean;
} => {
    const [preferences, setPreferences] = useState<AppPreferences>(defaultPreferences);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Define loadPreferences as a callback to prevent unnecessary re-renders
    const loadPreferences = useCallback((): void => {
        setIsLoading(true);
        try {
            const storedPreferences = localStorage.getItem('appPreferences');
            if (storedPreferences) {
                // Parse stored preferences
                const parsedPrefs = JSON.parse(storedPreferences);
                
                // Validate the theme value to ensure it's either 'light' or 'dark'
                if (parsedPrefs.theme !== 'light' && parsedPrefs.theme !== 'dark') {
                    // If theme is invalid, set to default based on system preference
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    parsedPrefs.theme = prefersDark ? 'dark' : 'light';
                }
                
                // Set preferences with type-safe values
                setPreferences(parsedPrefs as AppPreferences);
            } else {
                // Set initial theme based on system preference for first-time users
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                const initialTheme = prefersDark ? 'dark' : 'light';
                const initialPreferences = { 
                    ...defaultPreferences, 
                    theme: initialTheme 
                } as AppPreferences;
                
                localStorage.setItem('appPreferences', JSON.stringify(initialPreferences));
                setPreferences(initialPreferences);
            }
        } catch (error) {
            console.error("Error loading preferences:", error);
            // Fallback to default preferences if there's an error
            setPreferences(defaultPreferences);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load preferences on mount
    useEffect(() => {
        loadPreferences();
    }, [loadPreferences]);

    // Function to update default division
    const setDefaultDivision = (division: 'A Division' | 'B Division'): void => {
        const updatedPreferences = { ...preferences, defaultDivision: division };

        // Save to localStorage
        try {
            localStorage.setItem('appPreferences', JSON.stringify(updatedPreferences));
            setPreferences(updatedPreferences);
        } catch (error) {
            console.error("Error saving preferences:", error);
        }
    };

    // Function to update default sorting
    const setDefaultSorting = (sorting: 'Newest First' | 'Oldest First' | 'Alphabetical'): void => {
        const updatedPreferences = { ...preferences, defaultSorting: sorting };

        // Save to localStorage
        try {
            localStorage.setItem('appPreferences', JSON.stringify(updatedPreferences));
            setPreferences(updatedPreferences);
        } catch (error) {
            console.error("Error saving preferences:", error);
        }
    };

    // Function to update theme
    const setTheme = (theme: 'light' | 'dark'): void => {
        const updatedPreferences = { ...preferences, theme };

        // Save to localStorage
        try {
            localStorage.setItem('appPreferences', JSON.stringify(updatedPreferences));
            setPreferences(updatedPreferences);
        } catch (error) {
            console.error("Error saving theme preference:", error);
        }
    };

    return {
        preferences,
        setDefaultDivision,
        setDefaultSorting,
        setTheme,
        loadPreferences,
        isLoading
    };
};